import { storedClaims } from "@common/api";
import type { SyncRecords } from "./syncPlan";

// What cloud sync remembers between sessions, in localStorage.

// Per game: for each file, the cloud copy this browser last matched. With
// it, a file that changed only here, only in the cloud, or on both sides
// can be told apart; without it every file in both places is a conflict.
const RECORDS_PREFIX = "pocket-play.cloudSync.";

export function loadRecords(rom: string): SyncRecords {
  try {
    return (JSON.parse(localStorage.getItem(RECORDS_PREFIX + rom) ?? "{}") as SyncRecords) ?? {};
  } catch {
    return {};
  }
}

export function saveRecords(rom: string, records: SyncRecords) {
  localStorage.setItem(RECORDS_PREFIX + rom, JSON.stringify(records));
}

export function forgetRecords(rom: string) {
  localStorage.removeItem(RECORDS_PREFIX + rom);
}

/** Every game this browser has synced with the cloud. */
export function syncedRoms(): string[] {
  return Object.keys(localStorage)
    .filter((key) => key.startsWith(RECORDS_PREFIX))
    .map((key) => key.slice(RECORDS_PREFIX.length));
}

// Games, slots and saves live in browser storage that every account on this
// browser shares. The first account to sync owns them; auto-sync stays off
// for any other account so one person's saves never reach another's cloud,
// and their cloud saves never overwrite the local ones.
export const OWNER_KEY = "pocket-play.localOwner";

export function signedInSub(): string | null {
  const sub = storedClaims()?.sub;
  return typeof sub === "string" ? sub : null;
}

/** Whether the signed-in account may move this browser's saves to or from the cloud. */
export function ownsLocalSaves(): boolean {
  const owner = localStorage.getItem(OWNER_KEY);
  const sub = signedInSub();
  return !!sub && (!owner || owner === sub);
}

/** Makes the signed-in account the owner, once it has proved it can use the cloud. */
export function claimLocalSaves() {
  const sub = signedInSub();
  if (sub && !localStorage.getItem(OWNER_KEY)) localStorage.setItem(OWNER_KEY, sub);
}
